import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import {
  MAX_SLIDER_VALUE,
  MIN_SLIDER_VALUE,
  NEGATIVE_SLIDER_DEFAULT_VALUE,
  POSITIVE_SLIDER_DEFAULT_VALUE,
} from "../../../Constants"
import type { ConfigFormikProps, ThresholdSlidersFieldProps } from "../../../Interfaces"
import { BaseSlider } from "../../../Slider/BaseSlider"

import styles from "./ThresholdSlidersFields.module.scss"

/**
 * A component that displays two threshold slider fields for positive and negative labels.
 * It uses the `useFormikContext` hook to get the current values of the positive and negative thresholds.
 * It renders two sliders with labels and a section to display uncovered values.
 * The uncovered values are calculated based on the difference between the positive and negative threshold values.
 * @param {ThresholdSlidersFieldProps} props - The props for the component.
 * @return {ReactElement} A React component that displays the two threshold slider fields.
 */
export function ThresholdSlidersField(props: Readonly<ThresholdSlidersFieldProps>): ReactElement {
  const { onChangeHandler, disabled } = props

  const {
    values: { negativeThreshold, positiveThreshold },
  } = useFormikContext<ConfigFormikProps>()
  const { t } = useTranslation("ModelConfigurations")

  return (
    <section className={styles.slidersSection}>
      <div className={styles.uncoveredText}>
        <Typography variant="label-bold">{t("thresholdsTitle")}</Typography>
        {positiveThreshold - negativeThreshold > 1 && (
          <Typography variant="label-bold" variantColor={2}>
            {t("uncovered")} :{" "}
            {negativeThreshold + 1 === positiveThreshold - 1
              ? positiveThreshold - 1
              : `${negativeThreshold + 1}-${positiveThreshold - 1}`}
          </Typography>
        )}
      </div>

      <div className={styles.sliderContainer}>
        <BaseSlider
          variant="positive"
          sliderProps={{
            onChange: onChangeHandler,
            value: positiveThreshold,
            disabled: disabled,
            min: MIN_SLIDER_VALUE,
            max: MAX_SLIDER_VALUE,
            defaultValue: POSITIVE_SLIDER_DEFAULT_VALUE,
            marks: [
              { value: MIN_SLIDER_VALUE, label: MIN_SLIDER_VALUE },
              { value: MAX_SLIDER_VALUE, label: MAX_SLIDER_VALUE },
            ],
          }}
        />
        <Typography variant="label-bold" align="right">
          {t("thresholds.positiveThreshold")}
        </Typography>
      </div>
      <BaseSlider
        variant="negative"
        sliderProps={{
          onChange: onChangeHandler,
          value: negativeThreshold,
          disabled: disabled,
          min: MIN_SLIDER_VALUE,
          max: MAX_SLIDER_VALUE,
          defaultValue: NEGATIVE_SLIDER_DEFAULT_VALUE,
          marks: [
            { value: MIN_SLIDER_VALUE, label: MIN_SLIDER_VALUE },
            { value: MAX_SLIDER_VALUE, label: MAX_SLIDER_VALUE },
          ],
        }}
      />
      <Typography variant="label-bold">{t("thresholds.negativeThreshold")}</Typography>
    </section>
  )
}
